import React from 'react';
import { useSelector } from 'react-redux';
import { generateEventSourceByPageType } from '../../common/analytics/helpers/analyticsSourceHelper';
import {
  trackArticle,
  trackArticleFeedShowMore,
  trackArticleFeedTab,
  trackHeroArticle,
  trackMostPopularArticle,
  trackViewMoreLink,
} from '../../common/analytics/analyticsTracker';
import { ANALYTICS_FEATURES } from '../../common/analytics/featureTypes';
import config from '../../common/config';
import { AdUnits } from '../../common/helpers/ads/dfp';
import { fetchGuidesLandingPageData } from '../../common/helpers/data/landingPages/fetchNewsGuidesDecorLandingPageData';
import { Content } from '../../common/helpers/data/landingPages/fetchPostsData';
import { fetchLandingPageTopContentData } from '../../common/helpers/data/landingPages/fetchTopContentData';
import {
  getLandingPageType,
  LandingPageTypes,
} from '../../common/helpers/domain/landingPages';
import {
  getSubcategories,
  PostMainCategory,
  PostSubcategory,
} from '../../common/helpers/domain/postCategories';
import logger from '../../common/helpers/logger/logger';
import { extractPostIds } from '../../components/articleFeed/articleFeedHelper';
import { getGuidesTabDefinition } from '../../components/articleFeed/articleFeedTab';
import { Breadcrumb } from '../../components/global/breadcrumb/Breadcrumb';
import { PostList } from '../../components/global/PostList/PostList';
import HeroArticle from '../../components/homePage/heroArticle/HeroArticle';
import HomeSeoHeader from '../../components/homePage/homeSeoHeader/HomeSeoHeader';
import { ModuleTitle } from '../../components/homePage/moduleTitle/ModuleTitle';
import { generatePostMainCategoryBreadcrumbList } from '../../components/landingPage/breadcrumb';
import PostListModule from '../../components/landingPage/PostListModule';
import BillboardAd from '../../components/landingPageAds/BillboardAd';
import MainTitle from '../../components/mainTitle/MainTitle';
import MostPopularPosts from '../../components/mostPopular/MostPopularPosts';
import { DynamicComponentForArticleFeedModule } from '../../components/pages/homePage/DynamicComponents';
import {
  AsideContainer,
  LeftContainer,
  TopContentWrapper,
} from '../../components/pages/homePage/HomePage.style';
import { homePageRowSetting } from '../../components/rows/homePageRowSetting';
import Row from '../../components/rows/Row';
import SeoFooter from '../../components/seoFooter/SeoFooter';
import ViewMoreLink from '../../components/ViewMoreLink/ViewMoreLink';
import Page from '../../layouts/main';
import { CustomNextPageContext } from '../../lib/customNextPageContext';
import commonResource from '../../locales/th/common.json';
import homeResource from '../../locales/th/home.json';
import { HeroArticleInfo } from '../../queries/homePage/heroArticles';
import { MostPopular } from '../../queries/homePage/mostPopular';
import { updatePageType } from '../../store/home/slice';
import { updateCurrentSourcePage } from '../../store/search/slice';
import { WholeState } from '../../store/common/type';
import { getAdSectByPageType } from '../../common/helpers/ads/AdsHelper';

interface LocationInsightLandingPageProps {
  canonicalLink: string;
  heroArticle: HeroArticleInfo;
  popularArticles: MostPopular[];
  buyAndSellPosts: Content[];
  financialPosts: Content[];
  investmentPosts: Content[];
}

function GuidesLandingPage({
  canonicalLink,
  heroArticle,
  popularArticles,
  buyAndSellPosts,
  financialPosts,
  investmentPosts,
}: LocationInsightLandingPageProps) {
  const moduleResource = commonResource.postModules.guides;
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );
  const landingPageType = LandingPageTypes.GuidesPage;
  const analyticsSource = generateEventSourceByPageType(landingPageType);

  return (
    <Page
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      source={analyticsSource}
    >
      <HomeSeoHeader pageType={landingPageType} canonicalLink={canonicalLink} />
      <Breadcrumb
        items={generatePostMainCategoryBreadcrumbList(PostMainCategory.Guides)}
      />
      <Row verticalSpacing={0} maxWidth={homePageRowSetting.maxWidth}>
        <MainTitle
          title={homeResource.title.guides}
          subtitle={homeResource.subtitle.guides}
        />
      </Row>
      <Row
        verticalSpacing={0}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <TopContentWrapper>
          <LeftContainer>
            <HeroArticle
              heroArticleData={heroArticle}
              onClickHeroArticle={(heroArticle) =>
                trackHeroArticle(
                  userInfo,
                  heroArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_ARTICLE,
                )
              }
            />
          </LeftContainer>
          <AsideContainer>
            <MostPopularPosts
              post={popularArticles}
              onClick={(popularArticle) =>
                trackMostPopularArticle(
                  userInfo,
                  popularArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.POPULAR,
                )
              }
            />
          </AsideContainer>
        </TopContentWrapper>
      </Row>
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard1}
          sect={getAdSectByPageType(landingPageType)}
        />
      </Row>
      {buyAndSellPosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="buy-and-sell"
        >
          <PostListModule
            title={moduleResource.buyAndSell.title}
            subtitle={moduleResource.buyAndSell.subtitle}
            viewMoreLink={
              <ViewMoreLink
                landingPageType={getLandingPageType([
                  PostSubcategory.BuyAndSell,
                ])}
                onClick={(targetLandingPage) => {
                  trackViewMoreLink(
                    userInfo,
                    analyticsSource,
                    targetLandingPage,
                    ANALYTICS_FEATURES.LATEST_BUY_AND_SELL,
                  );
                }}
              />
            }
          >
            <PostList
              contents={buyAndSellPosts}
              adUnit={AdUnits.HP_Pos1}
              adSect={getAdSectByPageType(landingPageType)}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.LATEST_BUY_AND_SELL,
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      {financialPosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="financial"
        >
          <PostListModule
            title={moduleResource.financial.title}
            subtitle={moduleResource.financial.subtitle}
            viewMoreLink={
              <ViewMoreLink
                landingPageType={getLandingPageType([PostSubcategory.Finance])}
                onClick={(targetLandingPage) => {
                  trackViewMoreLink(
                    userInfo,
                    analyticsSource,
                    targetLandingPage,
                    ANALYTICS_FEATURES.LATEST_FINANCIAL,
                  );
                }}
              />
            }
          >
            <PostList
              contents={financialPosts}
              adUnit={AdUnits.HP_Pos2}
              adSect={getAdSectByPageType(landingPageType)}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.LATEST_FINANCIAL,
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      {investmentPosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="investment"
        >
          <PostListModule
            title={moduleResource.investment.title}
            subtitle={moduleResource.investment.subtitle}
            viewMoreLink={
              <ViewMoreLink
                landingPageType={getLandingPageType([
                  PostSubcategory.Investment,
                ])}
                onClick={(targetLandingPage) => {
                  trackViewMoreLink(
                    userInfo,
                    analyticsSource,
                    targetLandingPage,
                    ANALYTICS_FEATURES.LATEST_INVESTMENT,
                  );
                }}
              />
            }
          >
            <PostList
              contents={investmentPosts}
              adUnit={AdUnits.HP_Pos3}
              adSect={getAdSectByPageType(landingPageType)}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.LATEST_INVESTMENT,
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <ModuleTitle title={homeResource.readMore.title} />
        <DynamicComponentForArticleFeedModule
          tabDefinition={getGuidesTabDefinition()}
          excludePostIds={extractPostIds([
            ...buyAndSellPosts,
            ...financialPosts,
            ...investmentPosts,
          ])}
          adUnit={AdUnits.HP_MrecInfiniteAd}
          adSect={getAdSectByPageType(landingPageType)}
          onClickPostCard={(post) =>
            trackArticle(
              userInfo,
              post,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            )
          }
          onClickArticleFeedShowMore={() => {
            trackArticleFeedShowMore(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            );
          }}
          onClickArticleFeedTab={(selectedTab) => {
            trackArticleFeedTab(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
              selectedTab,
            );
          }}
        />
      </Row>
      <Row verticalSpacing={16} fullWidth>
        <SeoFooter />
      </Row>
    </Page>
  );
}

GuidesLandingPage.getInitialProps = async ({
  store,
  asPath,
  apolloClient,
}: CustomNextPageContext): Promise<LocationInsightLandingPageProps> => {
  const canonicalLink = config.baseUrl + asPath.substring(1);
  try {
    const { heroArticle, mostPopular } = await fetchLandingPageTopContentData(
      apolloClient,
      {
        postCategories: getSubcategories(PostMainCategory.Guides),
      },
    );

    const { buyAndSell, financial, investment } =
      await fetchGuidesLandingPageData(
        apolloClient,
        heroArticle?.id ? [heroArticle.id] : [],
      );

    const landingPageType = getLandingPageType(
      getSubcategories(PostMainCategory.Guides),
      undefined,
    );
    store.dispatch(updatePageType(landingPageType));
    store.dispatch(
      updateCurrentSourcePage(generateEventSourceByPageType(landingPageType)),
    );
    return {
      canonicalLink,
      heroArticle,
      popularArticles: mostPopular,
      buyAndSellPosts: buyAndSell,
      financialPosts: financial,
      investmentPosts: investment,
    };
  } catch (e) {
    logger.logError(
      'GuidesLandingPage.getInitialProps',
      'Error fetching data',
      e,
    );
  }
};

export default GuidesLandingPage;
